import { ImageName } from "@mcleod/images";
import { Api, DateUtil, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { ClickEvent, TableRowDisplayEvent, Component, LookupModelSearchEvent, BlurEvent, TableRow, Button, Label, ButtonVariant } from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AutogenLayoutServiceIncidentList } from "./autogen/AutogenLayoutServiceIncidentList";
import { ModelServiceIncidentOrders } from "./models/ModelServiceIncidentOrders";
import { LMEUserSettings } from "@mcleod/general";
import { OrderTabView } from "./OrderTabView";

export class ServiceIncidentList extends AutogenLayoutServiceIncidentList {
    override onLoad() {
        this.switchReportable.allowIndeterminate = true;
        this.switchReportable.checked = null;
        this.switchReviewRequired.allowIndeterminate = true;
        this.switchReviewRequired.checked = null;
        this.switchApproved.allowIndeterminate = true;
        this.switchApproved.checked = null;

        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp != null && urlProp.order_id != null) {
            this.textboxOrderId.text = urlProp.order_id;
            if (this.textboxStatus) {
                if (urlProp?.status) {
                    this.textboxStatus.setSelectedItemFromValue(urlProp.status);
                } else {
                    this.textboxStatus.setSelectedItemFromValue("Active");
                }
            }

            this.sourceServiceIncidentList.search({ order_id: urlProp.order_id, status: urlProp.status });
        }
    }

    /** This is an event handler for the onRowDisplay event of tableOffers.  */
    tableServiceIncidentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        tableRow.forEveryChildComponent(comp => {
            this.setTooltipContent(comp, tableRow.data);
            if(comp.id === "labelIsReportable") {
                const isReportable = tableRow.data.get("is_reportable");
                if (isReportable === "Y") {
                    comp.backgroundColor = "success";
                } else {
                    comp.backgroundColor = "error";
                }
            } else if (comp.id === "labelApprovalStatus" || comp.id === "buttonApprove") { //this label is used to sort by this column. it is removed and replaced with the button in code
                this.configureApproveButton(comp, tableRow.data);
            } else if (comp.id === "labelOrder") {
                this.setupOrderLink(comp as Label, tableRow.data.get("order_id"));
            }
        });
    }

    setupOrderLink(comp: Label, orderId) {
        comp.applyLinkStyle();
        comp.addClickListener(() => {
            OrderTabView.navigateTo({ key: orderId, newTab: true });
        });
    }
    
    setTooltipContent(comp: Component, modelRow: ModelRow) {
        switch (comp.id) {
            case "labelOrder":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("order_id"), "lme/dispatch/OrderQuickInfo", { minWidth: 128, minHeight: 140 });
            case "textboxStandardCodeDescription":
            case "labelStandardCodeDescription":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [{ caption: "Description", text: modelRow.get("edi_standard_code.description") }]);
            case "citystateStop":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    { caption: "Name", text: modelRow.get("stop.location_name") }, 
                    { caption: "Early Pickup", text: modelRow.get("stop.sched_arrive_early") },
                    { caption: "Late Pickup", text: modelRow.get("stop.sched_arrive_late") },
                    { caption: "Actual Arrival", text: modelRow.get("stop.actual_arrival") },
                    { caption: "Actual Departure", text: modelRow.get("stop.actual_departure") },
                    { caption: "Minutes Late", text: modelRow.get("minutes_late") },
                ]);
            case "labelCustomerName":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo", { minWidth: 128, minHeight: 140 });
            case "textboxPartnerId":
                return CommonTooltips.setTooltipContentLayout(comp, null, "lme/datafusion/EdiProfileStatusQuickInfo", null,
                    {
                        "direction": modelRow.get("edistatus_profile.direction"),
                        "partner_id": modelRow.get("edi_customer.partner_id"),
                        "version": modelRow.get("edistatus_profile.version"),
                    });
            case "labelCausedByValue":
                switch (modelRow.get("caused_by_type")) {
                    case "D": //driver
                    case "O": //other driver
                        return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver.id"), "lme/dispatch/asset/DriverQuickInfo", { minWidth: 128, minHeight: 140 });
                    case "C": //carrier
                        return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("payee.id"), "lme/powerbroker/CarrierQuickInfo", { minWidth: 128, minHeight: 140 });
                    case "U": //user
                        return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("caused_by_users.id"), "common/UserQuickInfo", { minWidth: 128, minHeight: 140 });
                    case "K": //terminal
                        return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("terminal_id"), "lme/dispatch/LocationQuickInfo", { minWidth: 128, minHeight: 140 });
                }
        }
    }

    configureApproveButton(comp, modelRow: ModelRow) {
        const button = new Button({ id: "buttonApprove", tooltip: "Approve Incident", variant: ButtonVariant.round, color: "caution", onClick: this.buttonApproveOnClick, imageName: ImageName.circleCheckEmpty, imageHeight: 24, imageWidth: 24 });

        if (modelRow.get("review_reqd") === "Y") {
            button.enabled = true;
        } else {
            button.enabled = false;
            button.imageName = ImageName.circleCheck;
            button.color = "success";
            
            const reviewedBy = modelRow.get("reviewed_by_users.name");
            
            if (reviewedBy) {
                const reviewDate = DateUtil.formatDateTime(new Date(modelRow.get("reviewed_date")), "yyyy-MM-dd HH:mm");
                button.tooltip = `Reviewed by ${reviewedBy} on ${reviewDate}`;
            } else {
                button.visible = false;
            }
        }

        comp.parent.replace(comp, button);
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxEdiStandardCode2. */
    textboxEdiStandardCode2BeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_enabled = 'Y'
    }

    /** This is an event handler for the onBlur event of textboxOrderId. */
    textboxOrderIdOnBlur(event: BlurEvent) {
        const orderId = this.textboxOrderId.text.trim()
        
        //check order exists and has movement(s)
        if (StringUtil.isEmptyString(orderId)) {
            return;
        }

        new ModelServiceIncidentOrders().searchSingle({id: orderId}).then(async (r) => {
            if (r == null) {
                this.textboxOrderId.validationWarning = "Specified order does not exist";
                return;
            } else {
                this.textboxOrderId.validationWarning = "";
            }
        })
    }

    /** This is an event handler for the onClick event of buttonApprove. */
    async buttonApproveOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const modelRow = (TableRow.getContainingTableRow(button)).data as ModelRow;

        if(await CommonDialogs.showYesNo("Are you sure you want to approve this incident?", "Approve Incident")) {
            const reviewDate = DateUtil.formatDateTime(new Date(), "yyyy-MM-dd HH:mm");
            Api.update("lme/dispatch/service-incident-list",
                {
                    "id": modelRow.get("id"),
                    "review_reqd": "N",
                    "reviewed_by": LMEUserSettings.getUserId(),
                    "reviewed_date": reviewDate
                }
            ).then(() => {
                button.busy = false;
                button.enabled = false;

                const reviewedBy = LMEUserSettings.getUserName();
                button.tooltip = `Reviewed by ${reviewedBy} on ${reviewDate}`;
                button.imageName = ImageName.circleCheck;
                button.color = "success";
            })
        } else {
            button.busy = false;
        }
    }
}
